/**
 * Puente con la página que contiene a ManiType cuando se muestra dentro de un
 * marco (p. ej. en el portafolio de pedrinidev.com).
 *
 * Dentro de un marco, la barra de direcciones del navegador enseña la página
 * de FUERA, no esta. Al navegar aquí la barra no se movía, recargar devolvía
 * a la pantalla inicial y el enlace no se podía compartir. La página de fuera
 * no puede leer dónde estamos —el navegador lo prohíbe entre orígenes—, así
 * que se lo decimos: en cada cambio de pantalla, un mensaje con la ruta y el
 * título. Ella decide qué hacer con él.
 *
 * Protocolo: `{ type: 'embed:route', path, title }` por `postMessage`.
 *
 * El mensaje va dirigido SOLO al origen que nos contiene, nunca a `*`: la ruta
 * puede llevar el código de una carrera o un nombre de usuario. Además, solo
 * puede contenernos quien `FRAME_ANCESTORS` autoriza, así que ese origen ya
 * es de confianza.
 */

export const ROUTE_MESSAGE = 'embed:route';
const STORAGE_KEY = 'manitype:parent-origin';

export interface ParentHints {
  framed: boolean;
  /** `location.ancestorOrigins[0]`: Chrome y Safari lo dan; Firefox, no. */
  ancestorOrigin: string | null;
  referrer: string;
  selfOrigin: string;
  /** Lo que se guardó en una carga anterior de esta misma pestaña. */
  stored: string | null;
}

/**
 * Origen de la página que nos contiene, o `null` si no hay ninguna o no se
 * puede saber con certeza.
 *
 * Por orden: `ancestorOrigins`, que es exacto; si falta (Firefox), el origen
 * del `referrer` de la primera carga, que el marco del portafolio envía como
 * origen; y si esta pestaña ya recargó el marco por dentro —y el referrer
 * pasó a ser la propia ManiType—, el que se guardó la primera vez. Si nada de
 * eso existe, no se anuncia nada: mejor callar que hablarle a quien no es.
 */
export function resolveParentOrigin(hints: ParentHints): string | null {
  if (!hints.framed) return null;
  if (hints.ancestorOrigin && hints.ancestorOrigin !== 'null') return hints.ancestorOrigin;
  if (hints.referrer) {
    try {
      const origin = new URL(hints.referrer).origin;
      if (origin !== hints.selfOrigin && origin !== 'null') return origin;
    } catch {
      /* referrer malformado: se sigue con lo guardado */
    }
  }
  return hints.stored && hints.stored !== 'null' ? hints.stored : null;
}

export function routeMessage(loc: Pick<Location, 'pathname' | 'search' | 'hash'>, title: string) {
  return { type: ROUTE_MESSAGE, path: loc.pathname + loc.search + loc.hash, title };
}

let parentOrigin: string | null | undefined;

function currentParentOrigin(): string | null {
  if (parentOrigin !== undefined) return parentOrigin;
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    /* almacenamiento bloqueado: se trabaja sin él */
  }
  parentOrigin = resolveParentOrigin({
    framed: window.parent !== window,
    ancestorOrigin: location.ancestorOrigins?.item(0) ?? null,
    referrer: document.referrer,
    selfOrigin: location.origin,
    stored,
  });
  if (parentOrigin) {
    try {
      sessionStorage.setItem(STORAGE_KEY, parentOrigin);
    } catch {
      /* ídem */
    }
  }
  return parentOrigin;
}

/** Avisa a la página contenedora de la pantalla actual. Fuera de un marco no hace nada. */
export function announceRoute(title: string): void {
  const origin = currentParentOrigin();
  if (!origin) return;
  window.parent.postMessage(routeMessage(location, title), origin);
}
